import { html2canvas } from "@/app/report/common/export/lazyLibs"
import { SectionPerformanceProfile } from "@/app/report/common/constructor.ts"
import { ActivityDetail, ActivityParticipatedPlayerType } from "@/types/activity.ts"
import { cloneDeep } from "lodash"

import "./performanceProfileSection.scss"
import SectionContainer from "../SectionContainer"
import SectionMenu from "@/app/report/components/SectionMenu.tsx"
import { DEFAULT_EXPORT_OPTIONS, ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants.ts"
import TimeRangeSliderContainer from "@/app/report/components/timeSlider/TimeRangeSliderContainer.tsx"
import { useState, useMemo, useEffect, useRef, useCallback } from "react"
import { TimeChangeData, OverlayRenderProps } from "@/app/report/components/timeSlider/timeTypes.ts"
import {
  useReportPlayerActivityActionsQuery,
  useReportPlayerActivityPerformanceQuery,
} from "@/hooks/api/useReportQuery.ts"
import { colors } from "@/components/colors"
import { Checkbox, FormControlLabel, Popover } from "@mui/material"
import { CheckboxChecked, CheckboxUnChecked } from "@/components/icon/svg"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "@/hooks/useSearchParams"
import { isEqualUnordered } from "@/app/report/common/tools.ts"

import SessionSelector from "@/app/report/components/filter/sessionSelector/SessionSelector"
import PlayerSelector from "@/app/report/components/filter/playerSelector/PlayerSelector"
import BasicButton from "@/components/button/BasicButton"

import SpeedGraph from "./SpeedGraph"
import PerformanceGraph from "./PerformanceGraph"
import { DEFAULT_ACTION_TYPES } from "@/app/report/common/tacticMapType.ts"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook.ts"
import { inlineStylesheetsIntoClone } from "@/app/report/common/export/inlineStylesheets"
import { PDF } from "@/app/report/common/export/pdf"
import { exportTableFromView } from "@/app/report/common/export/exportTable"
import { getDateTimeString } from "@/app/report/common/tools"
import { createAccountStore } from "@/stores/account"
import { AccountTypeKey } from "@/constants/account"

export default function PerformanceProfileSection({
  reportSection,
  loading,
  activity,
  idfSessions,
  idfPlayers,
}: {
  reportSection: SectionPerformanceProfile
  loading: boolean
  activity: ActivityDetail
  idfSessions: number[]
  idfPlayers: number[]
}) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const searchParams = useSearchParams()
  const dailyReportType = searchParams.get("dailyReportType") || ACTIVITY_SESSION_TYPE.SESSION
  const isActivityReport = dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY

  const filterAnchorRef = useRef<HTMLButtonElement>(null)
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const [popoverVerticalOrigin, setPopoverVerticalOrigin] = useState<"top" | "bottom">("bottom")
  const [popoverMaxHeight, setPopoverMaxHeight] = useState<string>("calc(100vh - 100px)")
  const [selectedSessionList, setSelectedSessionList] = useState<number[]>(idfSessions)
  const [selectedPlayerList, setSelectedPlayerList] = useState<number[]>(() => {
    if (idfPlayers.length === 0) return []

    const sortedPlayers = activity.players
      .filter((player) => idfPlayers.includes(player.idfPlayer))
      .sort((a, b) => a.uniformNumber - b.uniformNumber)

    return sortedPlayers.length > 0 ? [sortedPlayers[0].idfPlayer] : [idfPlayers[0]]
  })
  const [tempSelectedSessionList, setTempSelectedSessionList] = useState<number[]>([])
  const [tempSelectedPlayerList, setTempSelectedPlayerList] = useState<
    ActivityParticipatedPlayerType[]
  >([])
  const [selectedType, setSelectedType] = useState<ACTIVITY_SESSION_TYPE>(
    isActivityReport ? ACTIVITY_SESSION_TYPE.ACTIVITY : ACTIVITY_SESSION_TYPE.SESSION,
  )
  const [sessionSelectorOpen, setSessionSelectorOpen] = useState(false)
  const [playerSelectorOpen, setPlayerSelectorOpen] = useState(false)

  const contentRef = useRef<HTMLDivElement>(null)
  const prevSessionListRef = useRef<number[]>([])

  const [dataTypes] = useState<string[]>(reportSection.settings.yAxis.map((y) => y.dataType))
  const [actionTypes] = useState<string[]>(reportSection.settings.actions.map((y) => y.actionCode))

  const { data: activityPerformanceData } = useReportPlayerActivityPerformanceQuery({
    idfActivity: activity.idfActivity,
    idfPlayer: selectedPlayerList[0],
    dataTypes: dataTypes,
  })

  const { data: activityPerformanceActions } = useReportPlayerActivityActionsQuery({
    idfActivity: activity.idfActivity,
    idfPlayer: selectedPlayerList[0],
    actionTypes: actionTypes,
  })

  const performanceData = activityPerformanceData
  const performanceActions = activityPerformanceActions

  const downsampled1HzData = useMemo(() => {
    const downsample = (data: number[] | undefined) => {
      if (!data || data.length === 0) {
        return { values: [] as number[], points: [] as { value: number; timeIndex: number }[] }
      }

      const values: number[] = []
      const points: { value: number; timeIndex: number }[] = []

      for (let i = 0; i < data.length; i += 10) {
        const chunk = data.slice(i, Math.min(i + 10, data.length))
        const validValues = chunk.filter((v) => v !== null && v !== undefined)

        if (validValues.length > 0) {
          const avg = validValues.reduce((sum, v) => sum + v, 0) / validValues.length
          const middleIndex = i + Math.floor((Math.min(10, chunk.length) - 1) / 2)
          values.push(avg)
          points.push({ value: avg, timeIndex: middleIndex })
        }
      }

      return { values, points }
    }

    return {
      speed: downsample(performanceData?.speed),
      accel: downsample(performanceData?.accel),
      hr: downsample(performanceData?.hr),
    }
  }, [performanceData])

  const [sliderConfig, setSliderConfig] = useState({
    initialStartTime: 0,
    initialEndTime: 36000,
    maxTime: 72000,
    boundaryMin: 0,
    boundaryMax: 72000,
  })

  const [mainGraphTimeRange, setMainGraphTimeRange] = useState({
    start: 0,
    end: 36000,
  })

  const [enabledActions, setEnabledActions] = useState<Record<string, boolean>>(
    reportSection.settings.actions.reduce(
      (acc, action) => {
        acc[action.actionCode] = true

        return acc
      },
      {} as Record<string, boolean>,
    ),
  )

  const exportPerformanceCSV = () => {
    if (!performanceData) {
      return
    }

    const { speed, accel, hr } = downsampled1HzData
    const enabledTypes = reportSection.settings.yAxis.map((y) => y.dataType)

    const headers: { key: string; title: string; unit?: string }[] = [
      { key: "time", title: t("time", "Time"), unit: "s" },
    ]

    if (enabledTypes.includes("speed")) {
      headers.push({ key: "speed", title: translateKey("speed") || "Speed", unit: "km/h" })
    }
    if (enabledTypes.includes("accel")) {
      headers.push({
        key: "accel",
        title: translateKey("accel") || "Acceleration",
        unit: "m/s²",
      })
    }
    if (enabledTypes.includes("hr")) {
      headers.push({ key: "hr", title: translateKey("hr") || "Heart Rate", unit: "bpm" })
    }

    const basePoints = speed.points.length > 0 ? speed : accel.points.length > 0 ? accel : hr
    const speedByTime = new Map(speed.points.map((p) => [p.timeIndex, p.value]))
    const accelByTime = new Map(accel.points.map((p) => [p.timeIndex, p.value]))
    const hrByTime = new Map(hr.points.map((p) => [p.timeIndex, p.value]))
    const rows = basePoints.points
      .map((pt) => ({
        time: (pt.timeIndex / 10).toFixed(1),
        speed: speedByTime.get(pt.timeIndex) ?? null,
        accel: accelByTime.get(pt.timeIndex) ?? null,
        hr: hrByTime.get(pt.timeIndex) ?? null,
      }))
      .filter((row) => {
        const timeCenti = parseFloat(row.time) * 10

        return timeCenti >= mainGraphTimeRange.start && timeCenti <= mainGraphTimeRange.end
      })

    const selectedPlayer = activity.players.find((p) => p.idfPlayer === selectedPlayerList[0])
    const playerName = selectedPlayer?.name || ""
    const activityName = activity.name || ""
    const fileName = `${getDateTimeString()}_Activity_${activityName}_${playerName}_PerformanceProfile`

    exportTableFromView({ fileName, headers, rows })
  }

  const exportPerformanceActionsCSV = () => {
    const actions = (performanceActions?.actions || []).filter((action) => {
      return (
        enabledActions[action.actionType] &&
        action.startOccurrence >= mainGraphTimeRange.start &&
        action.startOccurrence <= mainGraphTimeRange.end
      )
    })

    if (actions.length === 0) {
      return
    }

    const headers: { key: string; title: string; unit?: string }[] = [
      { key: "actionType", title: t("actionType", "Action Type") },
      { key: "start", title: t("start", "Start"), unit: "s" },
      { key: "end", title: t("end", "End"), unit: "s" },
      { key: "duration", title: t("duration", "Duration"), unit: "s" },
    ]

    const rows = actions.map((action) => ({
      actionType:
        translateKey(
          DEFAULT_ACTION_TYPES.find((a) => a.value === action.actionType)?.label || "",
        ) || action.actionType,
      start: (action.startOccurrence / 10).toFixed(1),
      end: (action.endOccurrence / 10).toFixed(1),
      duration: (action.duration / 10).toFixed(2),
    }))

    const selectedPlayer = activity.players.find((p) => p.idfPlayer === selectedPlayerList[0])
    const playerName = selectedPlayer?.name || ""
    const activityName = activity.name || ""
    const fileName = `${getDateTimeString()}_Activity_${activityName}_${playerName}_PerformanceProfile_Actions`

    exportTableFromView({ fileName, headers, rows })
  }

  const getMenuItems = () => {
    const exportOptions = DEFAULT_EXPORT_OPTIONS.map((option) => ({
      ...option,
      func: async () => {
        await handleExport(option.value as "png" | "pdf")
      },
    }))

    const csvStatsOption = {
      value: "csvStats",
      name: "CSV (Stats)",
      group: "button.export",
      func: exportPerformanceCSV,
    }

    const csvActionsOption = {
      value: "csvActions",
      name: "CSV (Actions)",
      group: "button.export",
      func: exportPerformanceActionsCSV,
    }

    return [...exportOptions, csvStatsOption, csvActionsOption]
  }

  const handleExport = async (exportType: "png" | "pdf") => {
    if (!contentRef.current) {
      return
    }

    const valueLabels = document.querySelectorAll(".MuiSlider-valueLabel")
    valueLabels.forEach((label) => {
      ;(label as HTMLElement).style.display = "none"
    })

    const sliderInputs = contentRef.current.querySelectorAll('.MuiSlider-thumb input[type="range"]')
    sliderInputs.forEach((input) => {
      ;(input as HTMLElement).style.display = "none"
    })

    await new Promise((resolve) => setTimeout(resolve, 50))

    try {
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: colors.neutral.white,
        scale: 2,
        onclone: inlineStylesheetsIntoClone,
        logging: false,
        useCORS: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        ignoreElements: (element) => {
          return (
            element.classList.contains("MuiSlider-valueLabel") ||
            element.classList.contains("MuiSlider-valueLabelLabel")
          )
        },
      })

      const fileName = `${getDateTimeString()}_daily_report_performance_profile`

      if (exportType === "png") {
        const link = document.createElement("a")
        link.download = `${fileName}.png`
        link.href = canvas.toDataURL("image/png")
        link.click()
      } else {
        const pdf = await PDF.create()
        pdf.appendCanvas(canvas)
        pdf.save(`${fileName}.pdf`)
      }
    } catch (error) {
      console.error("Export failed:", error)
    } finally {
      valueLabels.forEach((label) => {
        ;(label as HTMLElement).style.display = ""
      })

      sliderInputs.forEach((input) => {
        ;(input as HTMLElement).style.display = ""
      })
    }
  }

  const handleTimeChange = (timeData: TimeChangeData): void => {
    setSliderConfig((prev) => ({
      ...prev,
      initialStartTime: timeData.start,
      initialEndTime: timeData.end,
    }))

    setMainGraphTimeRange({
      start: timeData.start,
      end: timeData.end,
    })
  }

  const handleTimeSliding = useCallback((): void => {
    // 슬라이딩 끝나고만 업데이트.
  }, [])

  const handleGraphDrag = (timeRange: { start: number; end: number }): void => {
    setMainGraphTimeRange(timeRange)
    setSliderConfig((prev) => ({
      ...prev,
      initialStartTime: timeRange.start,
      initialEndTime: timeRange.end,
    }))
  }

  useEffect(() => {
    if (idfSessions.length && idfPlayers.length) {
      setSelectedSessionList(idfSessions)

      const sortedPlayers = activity.players
        .filter((player) => idfPlayers.includes(player.idfPlayer))
        .sort((a, b) => a.uniformNumber - b.uniformNumber)

      if (sortedPlayers.length > 0) {
        setSelectedPlayerList([sortedPlayers[0].idfPlayer])
      }
    }
  }, [idfSessions, idfPlayers, activity.players])

  useEffect(() => {
    if (filterOpen && activity?.sessions?.length && selectedSessionList?.length) {
      const filteredSessions = activity.sessions
        .filter((s) => selectedSessionList.includes(s.idfSession))
        .map((s) => s.idfSession)
      setTempSelectedSessionList(filteredSessions)
    }
  }, [filterOpen, activity, selectedSessionList])

  useEffect(() => {
    if (filterOpen && activity?.players?.length) {
      const accountInfo = createAccountStore.getState().info
      const isPlayerAccount = accountInfo?.type === AccountTypeKey.Player
      const playerIdfAccount = accountInfo?.idfAccount

      if (selectedPlayerList?.length) {
        let filteredPlayers = activity.players.filter((p) =>
          selectedPlayerList.includes(p.idfPlayer),
        )

        if (isPlayerAccount && playerIdfAccount) {
          filteredPlayers = filteredPlayers.filter((p) => p.idfPlayer === playerIdfAccount)
        }

        setTempSelectedPlayerList(filteredPlayers)
      } else if (activity.players.length > 0) {
        const firstPlayer = activity.players.find((p) => idfPlayers.includes(p.idfPlayer))
        if (firstPlayer) {
          if (isPlayerAccount && playerIdfAccount && firstPlayer.idfPlayer !== playerIdfAccount) {
            const selfPlayer = activity.players.find((p) => p.idfPlayer === playerIdfAccount)
            if (selfPlayer) {
              setTempSelectedPlayerList([selfPlayer])
            }
          } else {
            setTempSelectedPlayerList([firstPlayer])
          }
        }
      }
    }
  }, [filterOpen, activity, selectedPlayerList, idfPlayers])

  useEffect(() => {
    setSelectedType(
      dailyReportType === ACTIVITY_SESSION_TYPE.SESSION
        ? ACTIVITY_SESSION_TYPE.SESSION
        : ACTIVITY_SESSION_TYPE.ACTIVITY,
    )
  }, [dailyReportType])

  const tempActivityDetail = useMemo(() => {
    const accountInfo = createAccountStore.getState().info
    const isPlayerAccount = accountInfo?.type === AccountTypeKey.Player
    const playerIdfAccount = accountInfo?.idfAccount

    let filteredPlayers = activity.players.filter((p) => idfPlayers.includes(p.idfPlayer))

    if (isPlayerAccount && playerIdfAccount) {
      filteredPlayers = filteredPlayers.filter((p) => p.idfPlayer === playerIdfAccount)
    }

    return {
      ...cloneDeep(activity),
      sessions:
        dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY
          ? []
          : activity.sessions.filter((s) => idfSessions.includes(s.idfSession)),
      players: filteredPlayers,
    }
  }, [activity, idfSessions, idfPlayers, dailyReportType])

  const handleApplyFilter = () => {
    setSelectedSessionList(tempSelectedSessionList)
    setSelectedPlayerList(tempSelectedPlayerList.map((p) => p.idfPlayer))
    setFilterOpen(false)
  }

  const handleResetFilter = () => {
    setTempSelectedSessionList(selectedSessionList)

    const currentSelectedPlayers = activity.players.filter((p) =>
      selectedPlayerList.includes(p.idfPlayer),
    )
    setTempSelectedPlayerList(currentSelectedPlayers)
  }

  const applyDisabled = useMemo(() => {
    const currentType =
      dailyReportType === ACTIVITY_SESSION_TYPE.SESSION
        ? ACTIVITY_SESSION_TYPE.SESSION
        : ACTIVITY_SESSION_TYPE.ACTIVITY

    return (
      selectedType === currentType &&
      isEqualUnordered(tempSelectedSessionList, selectedSessionList) &&
      isEqualUnordered(
        tempSelectedPlayerList.map((p) => p.idfPlayer),
        selectedPlayerList,
      )
    )
  }, [
    tempSelectedSessionList,
    tempSelectedPlayerList,
    selectedSessionList,
    selectedPlayerList,
    selectedType,
    dailyReportType,
  ])

  useEffect(() => {
    if (!activity || !performanceData) {
      return
    }

    const maxTime = performanceData.totalOccurrence
    const activityStartTime = new Date(activity.dateStart)

    if (!isActivityReport && !!activity.sessions.length && !!selectedSessionList.length) {
      const prevSessionList = prevSessionListRef.current
      const sessionListChanged =
        prevSessionList.length !== selectedSessionList.length ||
        !prevSessionList.every((id, index) => id === selectedSessionList[index])

      const filteredSessions = activity.sessions.filter((s) =>
        selectedSessionList.includes(s.idfSession),
      )

      if (filteredSessions.length > 0) {
        const sessionStartMills = Math.min(
          ...filteredSessions.map((s) => new Date(s.dateStart).getTime()),
        )
        const sessionFinishMills = Math.max(
          ...filteredSessions.map((s) => new Date(s.dateFinish).getTime()),
        )

        const relativeStartMillis = Math.floor(
          (sessionStartMills - activityStartTime.getTime()) / 100,
        )
        const relativeEndMillis = Math.floor(
          (sessionFinishMills - activityStartTime.getTime()) / 100,
        )

        if (sessionListChanged) {
          setSliderConfig((prev) => ({
            ...prev,
            initialStartTime: Math.max(0, relativeStartMillis),
            initialEndTime: Math.min(maxTime, relativeEndMillis),
            maxTime: maxTime,
            boundaryMin: Math.max(0, relativeStartMillis),
            boundaryMax: Math.min(maxTime, relativeEndMillis),
          }))
          setMainGraphTimeRange({
            start: Math.max(0, relativeStartMillis),
            end: Math.min(maxTime, relativeEndMillis),
          })
          prevSessionListRef.current = [...selectedSessionList]
        } else {
          setSliderConfig((prev) => ({
            ...prev,
            maxTime: maxTime,
            boundaryMin: Math.max(0, relativeStartMillis),
            boundaryMax: Math.min(maxTime, relativeEndMillis),
          }))
        }

        return
      }
    }

    setSliderConfig({
      initialStartTime: 0,
      initialEndTime: maxTime,
      maxTime: maxTime,
      boundaryMin: 0,
      boundaryMax: maxTime,
    })
    setMainGraphTimeRange({
      start: 0,
      end: maxTime,
    })
    prevSessionListRef.current = []
  }, [performanceData, activity, selectedSessionList, isActivityReport])

  const renderSpeedGraphOverlay = useCallback(
    ({ currentStart, currentEnd, maxTime }: OverlayRenderProps) => {
      if (downsampled1HzData.speed.points.length === 0) {
        return null
      }

      const selectionStart = currentStart / maxTime
      const selectionEnd = currentEnd / maxTime

      return (
        <SpeedGraph
          data={downsampled1HzData.speed.points}
          height={36}
          color={colors.platinum[900]}
          selectionStart={selectionStart}
          selectionEnd={selectionEnd}
          selectionColor={colors.platinum[200]}
          maxTime={maxTime}
        />
      )
    },
    [downsampled1HzData.speed.points],
  )

  const sessionsTimeRanges = useMemo(() => {
    if (!activity?.sessions || !activity.dateStart) {
      return []
    }

    const activityStartTime = new Date(activity.dateStart).getTime()

    return activity.sessions.map((session) => {
      const sessionStartTime = new Date(session.dateStart).getTime()
      const sessionEndTime = new Date(session.dateFinish).getTime()

      return {
        start: Math.floor((sessionStartTime - activityStartTime) / 100),
        end: Math.floor((sessionEndTime - activityStartTime) / 100),
        name: session.name || session.type || "Session",
      }
    })
  }, [activity])

  return (
    <div className="PerformanceProfileSection">
      <SectionContainer
        title={reportSection.title}
        sectionMenu={
          <div className="PerformanceProfileSectionMenuContainer">
            <div className="performance-profile-filter">
              <BasicButton
                type="default-outlined"
                size="sm"
                icon="filter"
                ref={filterAnchorRef}
                onClick={() => {
                  if (filterAnchorRef.current) {
                    const rect = filterAnchorRef.current.getBoundingClientRect()
                    const spaceBelow = window.innerHeight - rect.bottom
                    const spaceAbove = rect.top
                    const shouldOpenUpward = spaceBelow < 500 && spaceAbove > spaceBelow
                    setPopoverVerticalOrigin(shouldOpenUpward ? "top" : "bottom")
                    setPopoverMaxHeight(
                      shouldOpenUpward
                        ? `${Math.min(rect.top - 20, 600)}px`
                        : `${Math.min(spaceBelow - 20, 600)}px`,
                    )
                  }
                  setFilterOpen(!filterOpen)
                }}>
                {t("filter")}
              </BasicButton>
              <Popover
                anchorEl={filterAnchorRef.current}
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                disableScrollLock
                anchorOrigin={{
                  vertical: popoverVerticalOrigin,
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: popoverVerticalOrigin === "bottom" ? "top" : "bottom",
                  horizontal: "left",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      maxHeight: popoverMaxHeight,
                      overflowY: "auto",
                    },
                  },
                }}
                sx={{
                  mt: popoverVerticalOrigin === "bottom" ? 1 : 0,
                  mb: popoverVerticalOrigin === "top" ? 1 : 0,
                  zIndex: 8,
                }}>
                <div className="performance-profile-filter-popover">
                  <div className="performance-profile-filter-menu performance-profile-session-filter">
                    <SessionSelector
                      open={sessionSelectorOpen}
                      setOpen={setSessionSelectorOpen}
                      selectedType={selectedType}
                      setSelectedType={setSelectedType}
                      selectedSessionList={tempSelectedSessionList}
                      setSelectedSessionList={setTempSelectedSessionList}
                      detail={tempActivityDetail}
                      hideRadioSelection={dailyReportType === ACTIVITY_SESSION_TYPE.SESSION}
                      disabled={dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY}
                    />
                  </div>
                  <div className="performance-profile-filter-menu performance-profile-player-filter">
                    <PlayerSelector
                      open={playerSelectorOpen}
                      setOpen={setPlayerSelectorOpen}
                      selectedActivity={[activity.idfActivity]}
                      players={tempActivityDetail?.players || []}
                      selectedPlayerList={tempSelectedPlayerList}
                      setSelectedPlayerList={setTempSelectedPlayerList}
                      paramsVersion={false}
                      singleSelection={true}
                    />
                  </div>
                  <div className="performance-profile-filter-buttons">
                    <div className="performance-profile-filter-reset-button">
                      <BasicButton type="primary-contained" size="sm" onClick={handleResetFilter}>
                        <img src="/images/ri-reset-left-line.png" width={20} height={20} alt="" />
                        <span>{t("button.reset")}</span>
                      </BasicButton>
                    </div>
                    <div className="performance-profile-filter-apply-button">
                      <BasicButton
                        type="primary-contained"
                        disabled={applyDisabled}
                        onClick={handleApplyFilter}>
                        <span className="performance-profile-filter-apply-button-text">
                          {t("button.apply")}
                        </span>
                      </BasicButton>
                    </div>
                  </div>
                </div>
              </Popover>
            </div>
            <SectionMenu menuItems={getMenuItems()} />
          </div>
        }
        loading={loading}>
        <div ref={contentRef}>
          <TimeRangeSliderContainer
            format="HH:MM:SS.ms"
            initialStartTime={sliderConfig.initialStartTime}
            initialEndTime={sliderConfig.initialEndTime}
            minDistance={1}
            maxTime={sliderConfig.maxTime}
            boundaryMin={sliderConfig.boundaryMin}
            boundaryMax={sliderConfig.boundaryMax}
            step={1}
            onTimeChange={handleTimeChange}
            onSliding={handleTimeSliding}
            overlayRender={renderSpeedGraphOverlay}
            overlayHeight={45}
            containerStyle={{
              margin: "0px auto 36px auto",
            }}
          />
          <div className="action-filters-container">
            {reportSection.settings.actions.map((action) => (
              <FormControlLabel
                key={action.actionCode}
                className="action-filter-checkbox"
                control={
                  <Checkbox
                    checked={enabledActions[action.actionCode] || false}
                    onChange={(e) => {
                      setEnabledActions((prev) => ({
                        ...prev,
                        [action.actionCode]: e.target.checked,
                      }))
                    }}
                    icon={<CheckboxUnChecked />}
                    checkedIcon={<CheckboxChecked />}
                    disableRipple
                    sx={{
                      "&.Mui-checked svg": {
                        fill: action.color,
                        color: "white",
                      },
                    }}
                  />
                }
                label={translateKey(
                  DEFAULT_ACTION_TYPES.find((a) => a.value === action.actionCode)?.label || "",
                )}
              />
            ))}
          </div>
          <div className="PerformanceProfileSectionGraphContainer">
            {performanceData && (
              <PerformanceGraph
                performanceData={{
                  speed: performanceData.speed,
                  accel: performanceData.accel,
                  hr: performanceData.hr,
                }}
                actions={(performanceActions?.actions || [])
                  .filter(
                    (action) =>
                      enabledActions[action.actionType] &&
                      reportSection.settings.actions.some(
                        (a) => a.actionCode === action.actionType,
                      ),
                  )
                  .map((action) => ({
                    ...action,
                    label:
                      translateKey(
                        DEFAULT_ACTION_TYPES.find((a) => a.value === action.actionType)?.label ||
                          "",
                      ) || action.actionType,
                  }))}
                sessions={sessionsTimeRanges}
                totalOccurrence={performanceData.totalOccurrence}
                colors={{
                  speed:
                    reportSection.settings.yAxis.find((y) => y.dataType === "speed")?.color ||
                    colors.blue[700],
                  accel:
                    reportSection.settings.yAxis.find((y) => y.dataType === "accel")?.color ||
                    colors.orange[700],
                  hr:
                    reportSection.settings.yAxis.find((y) => y.dataType === "hr")?.color ||
                    colors.red[700],
                }}
                manualScale={{
                  speed: (() => {
                    const speedAxis = reportSection.settings.yAxis.find(
                      (y) => y.dataType === "speed",
                    )

                    return speedAxis?.manualScaleActive
                      ? {
                          active: true,
                          value: speedAxis.manualScaleValue || 100,
                        }
                      : undefined
                  })(),
                  accel: (() => {
                    const accelAxis = reportSection.settings.yAxis.find(
                      (y) => y.dataType === "accel",
                    )

                    return accelAxis?.manualScaleActive
                      ? {
                          active: true,
                          value: accelAxis.manualScaleValue || 10,
                        }
                      : undefined
                  })(),
                  hr: (() => {
                    const hrAxis = reportSection.settings.yAxis.find((y) => y.dataType === "hr")

                    return hrAxis?.manualScaleActive
                      ? {
                          active: true,
                          value: hrAxis.manualScaleValue || 200,
                        }
                      : undefined
                  })(),
                }}
                actionColors={reportSection.settings.actions.reduce(
                  (acc, action) => {
                    acc[action.actionCode] = action.color

                    return acc
                  },
                  {} as Record<string, string>,
                )}
                timeRange={mainGraphTimeRange}
                maxTime={sliderConfig.maxTime}
                boundaryMin={sliderConfig.boundaryMin}
                boundaryMax={sliderConfig.boundaryMax}
                onTimeRangeChange={handleGraphDrag}
                height={300}
                downsampled1HzData={downsampled1HzData}
              />
            )}
          </div>
        </div>
      </SectionContainer>
    </div>
  )
}
